<script lang="ts">
import { computed, defineComponent, provide, ref } from 'vue'
import { useRoute } from 'vue-router'

import AppSidebar from '@/components/atoms/sidebars/AppSidebar.vue'
import AppSidebarMobile from '@/components/atoms/sidebars/AppSidebarMobile.vue'
import AppHeader from '@/components/molecules/header/AppHeader.vue'
import WebphoneDraggable from '@/components/molecules/webphone-draggable/WebphoneDraggable.vue'
import { useShiftActivitySync } from '@/composables/useShiftActivitySync'
import { useAuthStore } from '@/stores/useAuthStore'
import { useWorkspaceStore } from '@/stores/useWorkspaceStore'
import AppActionBar from '@/components/molecules/actionBar/AppActionBar.vue'
import WhatsApp from '@/pages/whatsapp/WhatsappPage.vue'

export default defineComponent({
  components: {
    AppSidebar,
    AppSidebarMobile,
    AppHeader,
    WebphoneDraggable,
    AppActionBar,
    WhatsApp,
  },
  setup() {
    useShiftActivitySync()
    const isSidebarExpanded = ref(false)
    const isSidebarVisuallyExpanded = ref(false)
    provide('isSidebarExpanded', isSidebarExpanded)

    const route = useRoute()
    const currentPath = computed(() => route.fullPath)
    const isWhatsAppRoute = computed(() => route.path.startsWith('/whatsapp'))
    const authStore = useAuthStore()
    const workspace = useWorkspaceStore()

    return {
      isSidebarExpanded,
      isSidebarVisuallyExpanded,
      currentPath,
      isWhatsAppRoute,
      authStore,
      workspace,
    }
  },
})
</script>

<template>
  <div
    class="dashboard-shell flex w-full flex-col lg:flex-row"
    :class="isWhatsAppRoute ? 'h-[100dvh] min-h-0 overflow-hidden lg:min-h-screen' : 'min-h-screen'"
  >
    <div class="hidden lg:block h-full">
      <AppSidebar
        @update:expanded="isSidebarExpanded = $event"
        @update:visual-expanded="isSidebarVisuallyExpanded = $event"
      />
    </div>

    <div class="fixed inset-x-0 top-0 z-50 w-full shadow-md lg:hidden">
      <AppSidebarMobile />
    </div>

    <main
      class="flex-1 overflow-visible transition-all duration-300 lg:h-screen lg:overflow-hidden lg:flex lg:flex-col"
      :class="[
        { 'lg:ml-[200px]': isSidebarVisuallyExpanded, 'lg:ml-16': !isSidebarVisuallyExpanded },
        isWhatsAppRoute
          ? 'flex min-h-0 flex-col overflow-hidden pt-[4.5rem] lg:pt-0'
          : 'min-h-screen pt-[4.5rem] lg:pt-0',
      ]"
    >
      <AppHeader />

      <div
      class="relative mt-0 lg:h-auto lg:min-h-0 lg:flex-1"
        :class="
          isWhatsAppRoute
            ? 'min-h-0 flex-1 overflow-hidden lg:h-auto lg:px-3 lg:pb-3'
            : 'px-3 pb-3 overflow-visible lg:overflow-hidden'
        "
        :style="{
          '--sidebar-width': isSidebarVisuallyExpanded ? '200px' : '64px',
        }"
      >
        <div class="relative flex h-full min-h-0 w-full flex-col gap-4 lg:flex-row">
          <!-- Contenido principal -->
          <section
            class="dashboard-content-scroll order-2 min-h-0 min-w-0 flex-1 lg:order-1 lg:h-full"
            :class="
              isWhatsAppRoute
                ? 'h-full overflow-hidden'
                : 'overflow-visible lg:overflow-y-auto'
            "
          >
            <transition v-if="!isWhatsAppRoute" name="page" mode="out-in">
              <div :key="currentPath">
                <slot />
              </div>
            </transition>

            <div
              v-show="isWhatsAppRoute"
              class="h-full min-h-0 overflow-visible"
            >
              <WhatsApp />
            </div>
          </section>

          <!-- ActionBar derecha -->
          <div
            class="order-1 relative z-40 h-0 w-0 flex-none overflow-visible mt-0 lg:order-2 lg:mt-0 lg:h-full lg:w-16 lg:flex-[0_0_64px] lg:pt-0"
          >
            <AppActionBar />
          </div>
        </div>
      </div>
    </main>

    <WebphoneDraggable :sidebar-expanded="isSidebarVisuallyExpanded" />
  </div>
</template>

<style scoped>
.dashboard-shell {
  background-color: var(--p-surface-200);

  background-image:
    /* Luz principal: entra desde la esquina superior izquierda */
    radial-gradient(
      ellipse 65% 55% at 8% 4%,
      color-mix(in srgb, var(--p-surface-0) 88%, transparent) 0%,
      color-mix(in srgb, var(--p-surface-0) 42%, transparent) 35%,
      transparent 72%
    ),

    /* Rebote suave de la luz hacia el centro-superior */
    radial-gradient(
      ellipse 50% 35% at 38% 20%,
      color-mix(in srgb, var(--p-surface-0) 18%, transparent) 0%,
      transparent 72%
    ),

    /* Ligero reflejo primario en la zona derecha */
    radial-gradient(
      ellipse 55% 65% at 88% 42%,
      color-mix(in srgb, var(--p-primary-200) 18%, transparent) 0%,
      transparent 55%
    ),

    /* Profundidad muy sutil en la parte inferior izquierda */
    radial-gradient(
      ellipse 45% 45% at 12% 92%,
      color-mix(in srgb, var(--p-primary-100) 18%, transparent) 0%,
      transparent 78%
    ),

    /* Reflejo ambiental inferior derecho */
    radial-gradient(
      ellipse 55% 45% at 82% 92%,
      color-mix(in srgb, var(--p-surface-50) 18%, transparent) 0%,
      transparent 80%
    ),

    /* Fondo base: ligeramente más iluminado arriba-izquierda */
    linear-gradient(
      145deg,
      var(--p-surface-100) 0%,
      var(--p-primary-50) 22%,
      var(--p-surface-100) 72%,
      var(--p-surface-50) 100%
    );

  background-attachment: fixed;
  background-size: cover;
}
:global(html.dark .dashboard-shell) {
  background-color: var(--p-surface-950);
  background-image:
    radial-gradient(circle at 48% 38%, color-mix(in srgb, var(--p-primary-950) 58%, transparent) 0 13%, transparent 38%),
    radial-gradient(circle at 76% 14%, color-mix(in srgb, var(--p-primary-800) 34%, transparent) 0, transparent 37%),
    radial-gradient(circle at 18% 86%, color-mix(in srgb, var(--p-primary-700) 28%, transparent) 0, transparent 35%),
    linear-gradient(135deg, var(--p-surface-950) 0%, var(--p-primary-950) 52%, var(--p-surface-900) 100%);
}
</style>
